import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexTheme,
  ApexTitleSubtitle,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis
} from 'ng-apexcharts';

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, ViewChild } from '@angular/core';
import { SharedModule } from 'src/app/layouts/full/shared/shared.module';
import { TranslateService } from '@ngx-translate/core';
import { Table } from 'primeng/table';
import { User } from 'src/app/models/user.model';
import { Client } from 'src/app/models/client.model';
import { MonthlyChargeAccounting } from 'src/app/models/monthlyChargesAccounting';
import { MonthlyChargesAccountingService } from 'src/app/services/monthly-charges-accounting.service';
import { UserService } from 'src/app/services/user.service';
import { ClientsService } from 'src/app/services/clients.service';
import { Roles } from 'src/app/layouts/full/shared/Enums';
import { forkJoin, lastValueFrom } from 'rxjs';

export type yearlysalesChartOptions = {
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: any;
  theme: ApexTheme;
  tooltip: ApexTooltip;
  dataLabels: ApexDataLabels;
  labels: string[];
  legend: ApexLegend;
  title: ApexTitleSubtitle;
  colors: string[];
  markers: any;
  grid: ApexGrid;
  fill: ApexFill;
  responsive: ApexResponsive;
  plotOptions: ApexPlotOptions;
};

interface Column {
  field: string;
  header: string;
  customExportHeader?: string;
}

interface ExportColumn {
  title: string;
  dataKey: string;
}

@Component({
  selector: 'app-analytics',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './analytics.component.html',
  styleUrl: './analytics.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnalyticsComponent {
  @ViewChild('dt') dt: Table; // Referencia a la tabla
  @Input() distributorId: string

  user: User
  isLoading: boolean = false
  search = '';
  ColumnMode: string = "";
  clients: Client[] = []
  chargedToOptions: string[] = ['Client', 'Distributor']; // Opciones de filtro
  chargedToFilter: string = ''; // Valor inicial del filtro
  userPermissions: string[] = [];

  public monthlyChargesLineChart: Partial<yearlysalesChartOptions>;
  public monthlyChargesDonutChart: Partial<yearlysalesChartOptions>;

  public charts: {
    oldSubscriptionLineChart: Partial<yearlysalesChartOptions>,
    basicSubscriptionLineChart: Partial<yearlysalesChartOptions>,
    advancedSubscriptionLineChart: Partial<yearlysalesChartOptions>,
    proSubscriptionLineChart: Partial<yearlysalesChartOptions>
    threeDInsolesDesignSubscriptionLineChart: Partial<yearlysalesChartOptions>
    threeDAfoDesignSubscriptionLineChart: Partial<yearlysalesChartOptions>
    threeDSpinalBraceDesignSubscriptionLineChart: Partial<yearlysalesChartOptions>
  }

  monthlyChargesAccounting: MonthlyChargeAccounting[] = []
  monthlyChargesAccountingTableData: MonthlyChargeAccounting[] = []
  filteredMonthlyChargesAccountingTableData: MonthlyChargeAccounting[] = [];

  selectedTimeRange: string = '3 meses';

  cols!: Column[];
  exportColumns!: ExportColumn[];

  constructor(
    private monthlyChargesAccountingService: MonthlyChargesAccountingService,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private clientService: ClientsService,
    private translate: TranslateService
  ) {
    // Init charts with default data
    this.charts = {
      oldSubscriptionLineChart: {},
      basicSubscriptionLineChart: {},
      advancedSubscriptionLineChart: {},
      proSubscriptionLineChart: {},
      threeDInsolesDesignSubscriptionLineChart: {},
      threeDAfoDesignSubscriptionLineChart: {},
      threeDSpinalBraceDesignSubscriptionLineChart: {}
    };

    this.defaultCharts();
  }

  async ngOnInit(): Promise<void> {
    this.user = this.userService.user

    this.cols = [
      { field: 'clientName', header: this.translate.instant('CLIENT'), customExportHeader: this.translate.instant('CLIENT') },
      { field: 'yearMonth', header: this.translate.instant('MONTH') },
      { field: 'chargedTo', header: this.translate.instant('CHARGED_TO') },
      { field: 'calculatedBasic', header: this.translate.instant('BASIC') },
      { field: 'calculatedAdvanced', header: this.translate.instant('ADVANCED') },
      { field: 'calculatedPro', header: this.translate.instant('PRO') },
      { field: 'calculatedInsoles', header: this.translate.instant('INSOLES') },
      { field: 'calculatedAfo', header: this.translate.instant('AFO') },
      { field: 'calculatedSpinalBrace', header: this.translate.instant('SPINAL_BRACE') }
    ];

    this.exportColumns = this.cols.map((col) => ({ title: col.header, dataKey: col.field }));
  }

  async ngAfterViewInit(): Promise<void> {
    await this.checkUserPermissions();

    // Iniciamos gráficos con 3 meses por defecto
    this.getDistributorChargesWithMonthsBack(2)
    this.getClientTableDataWithMonthsBack(2)
  }

  async checkUserPermissions(): Promise<void> {
    this.userPermissions = await lastValueFrom(this.userService.getUserPermissionsById(this.user.ID))
  }

  defaultCharts(): void {

    this.charts.oldSubscriptionLineChart = {
      series: [
        {
          name: this.translate.instant('CLIENT'),
          data: [0]
        },
        {
          name: this.translate.instant('DISTRIBUTOR'),
          data: [0]
        }
      ],
      chart: {
        height: 350,
        type: "line",
        zoom: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: "straight"
      },
      title: {
        text: this.translate.instant('OLD_SUBSCRIPTION'),
        align: "left"
      },
      grid: {
        row: {
          colors: ["#f3f3f3", "transparent"],
          opacity: 0.5
        }
      },
      xaxis: {
        categories: [
          "Jan"
        ]
      }
    };

    this.charts.basicSubscriptionLineChart = {
      series: [
        {
          name: this.translate.instant('CLIENT'),
          data: [0]
        },
        {
          name: this.translate.instant('DISTRIBUTOR'),
          data: [0]
        }
      ],
      chart: {
        height: 350,
        type: "line",
        zoom: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: "straight"
      },
      title: {
        text: this.translate.instant('BASIC_SUBSCRIPTION'),
        align: "left"
      },
      grid: {
        row: {
          colors: ["#f3f3f3", "transparent"],
          opacity: 0.5
        }
      },
      xaxis: {
        categories: [
          "Jan"
        ]
      }
    };

    this.charts.advancedSubscriptionLineChart = {
      series: [
        {
          name: this.translate.instant('CLIENT'),
          data: [0]
        },
        {
          name: this.translate.instant('DISTRIBUTOR'),
          data: [0]
        }
      ],
      chart: {
        height: 350,
        type: "line",
        zoom: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: "straight"
      },
      title: {
        text: this.translate.instant('ADVANCED_SUBSCRIPTION'),
        align: "left"
      },
      grid: {
        row: {
          colors: ["#f3f3f3", "transparent"],
          opacity: 0.5
        }
      },
      xaxis: {
        categories: [
          "Jan"
        ]
      }
    };

    this.charts.proSubscriptionLineChart = {
      series: [
        {
          name: this.translate.instant('CLIENT'),
          data: [0]
        },
        {
          name: this.translate.instant('DISTRIBUTOR'),
          data: [0]
        }
      ],
      chart: {
        height: 350,
        type: "line",
        zoom: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: "straight"
      },
      title: {
        text: this.translate.instant('PRO_SUBSCRIPTION'),
        align: "left"
      },
      grid: {
        row: {
          colors: ["#f3f3f3", "transparent"],
          opacity: 0.5
        }
      },
      xaxis: {
        categories: [
          "Jan"
        ]
      }
    };

    this.charts.threeDInsolesDesignSubscriptionLineChart = {
      series: [
        {
          name: this.translate.instant('CLIENT'),
          data: [0]
        },
        {
          name: this.translate.instant('DISTRIBUTOR'),
          data: [0]
        }
      ],
      chart: {
        height: 350,
        type: "line",
        zoom: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: "straight"
      },
      title: {
        text: this.translate.instant('THREE_D_INSOLES_DESIGN_SUBSCRIPTION'),
        align: "left"
      },
      grid: {
        row: {
          colors: ["#f3f3f3", "transparent"],
          opacity: 0.5
        }
      },
      xaxis: {
        categories: [
          "Jan"
        ]
      }
    };

    this.charts.threeDAfoDesignSubscriptionLineChart = {
      series: [
        {
          name: this.translate.instant('CLIENT'),
          data: [0]
        },
        {
          name: this.translate.instant('DISTRIBUTOR'),
          data: [0]
        }
      ],
      chart: {
        height: 350,
        type: "line",
        zoom: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: "straight"
      },
      title: {
        text: this.translate.instant('THREE_D_AFO_DESIGN_SUBSCRIPTION'),
        align: "left"
      },
      grid: {
        row: {
          colors: ["#f3f3f3", "transparent"],
          opacity: 0.5
        }
      },
      xaxis: {
        categories: [
          "Jan"
        ]
      }
    };

    this.charts.threeDSpinalBraceDesignSubscriptionLineChart = {
      series: [
        {
          name: this.translate.instant('CLIENT'),
          data: [0]
        },
        {
          name: this.translate.instant('DISTRIBUTOR'),
          data: [0]
        }
      ],
      chart: {
        height: 350,
        type: "line",
        zoom: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: "straight"
      },
      title: {
        text: this.translate.instant('THREE_D_SPINAL_BRACE_DESIGN_SUBSCRIPTION'),
        align: "left"
      },
      grid: {
        row: {
          colors: ["#f3f3f3", "transparent"],
          opacity: 0.5
        }
      },
      xaxis: {
        categories: [
          "Jan"
        ]
      }
    };

  }

  getDistributorChargesWithMonthsBack(months: number): void {
    this.isLoading = true

    const userId = this.distributorId ?? this.user.ID;

    if (this.user.role === Roles.ROLE_DISTRIBUTOR || this.user.role === Roles.ROLE_ADMIN) {
      this.monthlyChargesAccountingService.findByMonthsBackAndDistributor(userId, months).subscribe((resp) => {
        this.monthlyChargesAccounting = resp;
        this.sort();
        this.prepareChartData();
        this.isLoading = false
        this.cdr.detectChanges();
      }, (err) => {
        console.error("Error getting data", err)
        this.isLoading = false
        this.cdr.detectChanges();
      })
    }
  }

  getClientTableDataWithMonthsBack(months: number): void {
    this.isLoading = true;

    const userId = this.distributorId ?? this.user.ID;
    const charges$ = this.monthlyChargesAccountingService.findByMonthsBackAndParentId(userId, months);

    const canViewClientName =
      this.user.role !== Roles.ROLE_ADMIN ||
      this.userPermissions.includes('analytics.detailed_by_distributor.view_clients_table.view_client_name');

    if (!canViewClientName) {
      // Si no puede ver los nombres de clientes, solo hacemos la request de charges
      charges$.subscribe({
        next: (charges) => {
          this.monthlyChargesAccountingTableData = [];

          charges.forEach(charge => {
            const [clientId] = charge.EntityMonthlyID.split('#');
            const clientName = null;

            this.addChargeIfExists(charge, 'chargesToClientsAccounting', 'Client', clientName);
            this.addChargeIfExists(charge, 'chargesToDistributorAccounting', 'Distributor', clientName);
          });

          this.filterTable();
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error("Error loading charges:", err);
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      });

      return;
    }

    // Si puede ver los nombres de clientes, hacemos ambas peticiones
    const clients$ =
      this.user.role === Roles.ROLE_DISTRIBUTOR
        ? this.clientService.getClients()
        : this.clientService.getClientsByDistributorId(userId);

    forkJoin([charges$, clients$]).subscribe({
      next: ([charges, clients]) => {
        this.clients = clients;
        const clientMap = new Map(clients.map(client => [client.ID, client.name]));
        this.monthlyChargesAccountingTableData = [];

        charges.forEach(charge => {
          const [clientId] = charge.EntityMonthlyID.split('#');
          const clientName = clientMap.get(clientId) || 'Unknown';

          this.addChargeIfExists(charge, 'chargesToClientsAccounting', 'Client', clientName);
          this.addChargeIfExists(charge, 'chargesToDistributorAccounting', 'Distributor', clientName);
        });

        this.filterTable();
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error("Error loading data:", err);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private addChargeIfExists(
    charge: any,
    key: 'chargesToClientsAccounting' | 'chargesToDistributorAccounting',
    chargedTo: 'Client' | 'Distributor',
    clientName: string
  ): void {
    const chargeData = charge[key];
    if (chargeData && Object.keys(chargeData).length > 0) {
      const newCharge = { ...charge };
      newCharge.clientName = clientName;
      newCharge.chargedTo = chargedTo;

      // Asignar campos específicos
      const fields = ['basic', 'advanced', 'pro', 'insolesDesign', 'afoDesign', 'spinalBraceDesign'];
      fields.forEach(field => {
        newCharge[key][field] = charge[key][field];
      });

      this.monthlyChargesAccountingTableData.push(newCharge);
    }
  }

  sort(): void {
    this.monthlyChargesAccounting.sort(this.compareByYearMonth)
  }

  filterTable(): void {
    const calculateBasic = (row: any) =>
      row.chargedTo === 'Client'
        ? row.chargesToClientsAccounting?.basic ?? 0
        : row.chargesToDistributorAccounting?.basic ?? 0;

    const calculateAdvanced = (row: any) =>
      row.chargedTo === 'Client'
        ? row.chargesToClientsAccounting?.advanced ?? 0
        : row.chargesToDistributorAccounting?.advanced ?? 0;

    const calculatePro = (row: any) =>
      row.chargedTo === 'Client'
        ? row.chargesToClientsAccounting?.pro ?? 0
        : row.chargesToDistributorAccounting?.pro ?? 0;

    const calculateInsoles = (row: any) =>
      row.chargedTo === 'Client'
        ? row.chargesToClientsAccounting?.insolesDesign ?? 0
        : row.chargesToDistributorAccounting?.insolesDesign ?? 0;

    const calculateAfo = (row: any) =>
      row.chargedTo === 'Client'
        ? row.chargesToClientsAccounting?.afoDesign ?? 0
        : row.chargesToDistributorAccounting?.afoDesign ?? 0;

    const calculateSpinalBrace = (row: any) =>
      row.chargedTo === 'Client'
        ? row.chargesToClientsAccounting?.spinalBraceDesign ?? 0
        : row.chargesToDistributorAccounting?.spinalBraceDesign ?? 0;

    if (this.search) {
      // Filtra y calcula todos los valores
      this.filteredMonthlyChargesAccountingTableData = this.monthlyChargesAccountingTableData
        .filter(row => row.clientName.toLowerCase().includes(this.search.toLowerCase()))
        .map(row => ({
          ...row,
          calculatedBasic: calculateBasic(row),
          calculatedAdvanced: calculateAdvanced(row),
          calculatedPro: calculatePro(row),
          calculatedInsoles: calculateInsoles(row),
          calculatedAfo: calculateAfo(row),
          calculatedSpinalBrace: calculateSpinalBrace(row)
        }));
    } else {
      // Calcula el valor para todos los datos si no hay texto de búsqueda
      this.filteredMonthlyChargesAccountingTableData = this.monthlyChargesAccountingTableData.map(row => ({
        ...row,
        calculatedBasic: calculateBasic(row),
        calculatedAdvanced: calculateAdvanced(row),
        calculatedPro: calculatePro(row),
        calculatedInsoles: calculateInsoles(row),
        calculatedAfo: calculateAfo(row),
        calculatedSpinalBrace: calculateSpinalBrace(row)
      }));
    }
  }

  compareByYearMonth(a: MonthlyChargeAccounting, b: MonthlyChargeAccounting): number {
    // Suponiendo que a.yearMonth y b.yearMonth están en el formato 'YYYY-MM'

    // Extraer el año y el mes de cada string
    const [yearA, monthA] = a.yearMonth.split("-").map(Number);
    const [yearB, monthB] = b.yearMonth.split("-").map(Number);

    // Primero comparar por el año
    if (yearA < yearB) {
      return -1;  // Si el año de A es menor que el de B
    } else if (yearA > yearB) {
      return 1;   // Si el año de A es mayor que el de B
    } else {
      // Si los años son iguales, comparar por el mes
      if (monthA < monthB) {
        return -1;  // Si el mes de A es menor que el de B
      } else if (monthA > monthB) {
        return 1;   // Si el mes de A es mayor que el de B
      } else {
        return 0;   // Si el mes es igual
      }
    }
  }

  // Función para manejar el cambio de selección de meses
  handleSelectedTimeRangeChange(selectedValue: string) {
    this.selectedTimeRange = selectedValue;

    if (this.selectedTimeRange === "3 meses") {
      this.getDistributorChargesWithMonthsBack(2)
      this.getClientTableDataWithMonthsBack(2)
    }

    if (this.selectedTimeRange === "6 meses") {
      this.getDistributorChargesWithMonthsBack(5)
      this.getClientTableDataWithMonthsBack(5)
    }

    if (this.selectedTimeRange === "12 meses") {
      this.getDistributorChargesWithMonthsBack(11)
      this.getClientTableDataWithMonthsBack(12)
    }
  }


  prepareChartData() {

    let dataForChart: {
      distributor: {
        old: number[],
        basic: number[],
        advanced: number[],
        pro: number[],
        insolesDesign: number[],
        afoDesign: number[],
        spinalBraceDesign: number[]
      },
      client: {
        old: number[],
        basic: number[],
        advanced: number[],
        pro: number[],
        insolesDesign: number[],
        afoDesign: number[],
        spinalBraceDesign: number[]
      }
    } = {
      client: {
        advanced: [],
        basic: [],
        old: [],
        pro: [],
        insolesDesign: [],
        afoDesign: [],
        spinalBraceDesign: []
      },
      distributor: {
        advanced: [],
        basic: [],
        old: [],
        pro: [],
        insolesDesign: [],
        afoDesign: [],
        spinalBraceDesign: []
      }
    }

    let months: string[] = []

    this.monthlyChargesAccounting.map((monthCharge) => {

      dataForChart.distributor.old.push(monthCharge.chargesToDistributorAccounting.old)
      dataForChart.distributor.basic.push(monthCharge.chargesToDistributorAccounting.basic)
      dataForChart.distributor.advanced.push(monthCharge.chargesToDistributorAccounting.advanced)
      dataForChart.distributor.pro.push(monthCharge.chargesToDistributorAccounting.pro)
      dataForChart.distributor.insolesDesign.push(monthCharge.chargesToDistributorAccounting.insolesDesign ? monthCharge.chargesToDistributorAccounting.insolesDesign : 0)
      dataForChart.distributor.afoDesign.push(monthCharge.chargesToDistributorAccounting.afoDesign ? monthCharge.chargesToDistributorAccounting.afoDesign : 0)
      dataForChart.distributor.spinalBraceDesign.push(monthCharge.chargesToDistributorAccounting.spinalBraceDesign ? monthCharge.chargesToDistributorAccounting.spinalBraceDesign : 0)

      dataForChart.client.old.push(monthCharge.chargesToClientsAccounting.old)
      dataForChart.client.basic.push(monthCharge.chargesToClientsAccounting.basic)
      dataForChart.client.advanced.push(monthCharge.chargesToClientsAccounting.advanced)
      dataForChart.client.pro.push(monthCharge.chargesToClientsAccounting.pro)
      dataForChart.client.insolesDesign.push(monthCharge.chargesToClientsAccounting.insolesDesign ? monthCharge.chargesToClientsAccounting.insolesDesign : 0)
      dataForChart.client.afoDesign.push(monthCharge.chargesToClientsAccounting.afoDesign ? monthCharge.chargesToClientsAccounting.afoDesign : 0)
      dataForChart.client.spinalBraceDesign.push(monthCharge.chargesToClientsAccounting.spinalBraceDesign ? monthCharge.chargesToClientsAccounting.spinalBraceDesign : 0)

      months.push(this.convertirFechaTexto(monthCharge.yearMonth))
    })


    this.charts.oldSubscriptionLineChart.series = [
      {
        name: this.translate.instant('DISTRIBUTOR'),
        data: dataForChart.distributor.old
      },
      {
        name: this.translate.instant('CLIENT'),
        data: dataForChart.client.old
      },
    ]
    this.charts.oldSubscriptionLineChart.xaxis = {
      categories: months
    }


    this.charts.basicSubscriptionLineChart.series = [
      {
        name: this.translate.instant('DISTRIBUTOR'),
        data: dataForChart.distributor.basic
      },
      {
        name: this.translate.instant('CLIENT'),
        data: dataForChart.client.basic
      },
    ]
    this.charts.basicSubscriptionLineChart.xaxis = {
      categories: months
    }

    this.charts.advancedSubscriptionLineChart.series = [
      {
        name: this.translate.instant('DISTRIBUTOR'),
        data: dataForChart.distributor.advanced
      },
      {
        name: this.translate.instant('CLIENT'),
        data: dataForChart.client.advanced
      },
    ]
    this.charts.advancedSubscriptionLineChart.xaxis = {
      categories: months
    }

    this.charts.proSubscriptionLineChart.series = [
      {
        name: this.translate.instant('DISTRIBUTOR'),
        data: dataForChart.distributor.pro
      },
      {
        name: this.translate.instant('CLIENT'),
        data: dataForChart.client.pro
      },
    ]
    this.charts.proSubscriptionLineChart.xaxis = {
      categories: months
    }

    this.charts.threeDInsolesDesignSubscriptionLineChart.series = [
      {
        name: this.translate.instant('DISTRIBUTOR'),
        data: dataForChart.distributor.insolesDesign
      },
      {
        name: this.translate.instant('CLIENT'),
        data: dataForChart.client.insolesDesign
      },
    ]
    this.charts.threeDInsolesDesignSubscriptionLineChart.xaxis = {
      categories: months
    }

    this.charts.threeDAfoDesignSubscriptionLineChart.series = [
      {
        name: this.translate.instant('DISTRIBUTOR'),
        data: dataForChart.distributor.afoDesign
      },
      {
        name: this.translate.instant('CLIENT'),
        data: dataForChart.client.afoDesign
      },
    ]
    this.charts.threeDAfoDesignSubscriptionLineChart.xaxis = {
      categories: months
    }

    this.charts.threeDSpinalBraceDesignSubscriptionLineChart.series = [
      {
        name: this.translate.instant('DISTRIBUTOR'),
        data: dataForChart.distributor.spinalBraceDesign
      },
      {
        name: this.translate.instant('CLIENT'),
        data: dataForChart.client.spinalBraceDesign
      },
    ]
    this.charts.threeDSpinalBraceDesignSubscriptionLineChart.xaxis = {
      categories: months
    }

  }

  convertirFechaTexto(fecha: string): string {
    // Verificamos que la fecha tenga el formato correcto 'YYYY-MM'
    if (fecha.length !== 7) {
      throw new Error("Formato de fecha inválido. Debe ser 'YYYY-MM'");
    }

    // Obtener el mes y el año de la cadena de fecha
    const [año, mes] = fecha.split("-").map(Number);

    // Crear un objeto de fecha utilizando el mes y el año
    const fechaObj = new Date(año, mes - 1); // Mes en JavaScript es 0-indexed

    // Obtener el nombre del mes localizado
    const nombreMes = fechaObj.toLocaleString(this.translate.currentLang || 'en-US', { month: 'short' });

    // Obtener los últimos dos dígitos del año
    const ultimosDosDigitosAño = año.toString().slice(-2);

    // Construir la cadena de fecha en el formato deseado (por ejemplo, "Mar 23'")
    const fechaTexto = `${nombreMes} ${ultimosDosDigitosAño}'`;

    return fechaTexto;
  }
}
